// The match and argue steps' decisions, pure (KULA-REVISE-WORKSHOP-PLAN.md
// §4 steps 4 and 5, corrected by §0c): what a match board is and when it is
// solved, the table of themes it unlocks, the compare summary, the position
// a learner's judgements and ranking point at (the edition's or pair's hint
// table, never code), which themes get a paragraph once they have chosen
// three or four, and the thesis built from their choices.

import type { WorkshopBlock, WorkshopChecks, WorkshopEdition, WorkshopHints, WorkshopHintClause, WorkshopPosition, WorkshopTopicSentence } from '../types';
import { seededOrder } from '../scoring';
import { pairContent } from './pair';
import { capitalise, copyFor, copyVars, fillCopy, numberWord } from './steps';

// ---------------------------------------------------------------------------
// The argument content for the chosen case studies: the edition's own for one
// case, the pair's (hand-written or generated) for two.
// ---------------------------------------------------------------------------

export interface ArgumentContent {
  context: string;
  topicSentences: WorkshopTopicSentence[][];
  positions: WorkshopPosition[];
  hints: WorkshopHints;
  checks: WorkshopChecks;
}

export function argumentContent(w: WorkshopBlock, chosen: string[]): ArgumentContent | null {
  if (chosen.length === 2) {
    const p = pairContent(w, [chosen[0], chosen[1]]);
    return p ? { context: p.context, topicSentences: p.topicSentences, positions: p.positions, hints: p.hints, checks: p.checks } : null;
  }
  const e = chosen[0] ? w.editions[chosen[0]] : undefined;
  if (!e || !e.topicSentences || !e.positions || !e.hints) return null;
  return { context: e.context, topicSentences: e.topicSentences, positions: e.positions, hints: e.hints, checks: e.checks };
}

// ---------------------------------------------------------------------------
// Step 4: match each theme to the evidence that shows it.
// ---------------------------------------------------------------------------

/** The order the evidence column is dealt in, fixed per learner session and board. */
export const matchOrder = (n: number, seed: string): number[] => seededOrder(n, seed);

export interface MatchCheck {
  /** Themes left unmatched. */
  missing: number;
  correct: number;
  total: number;
  solved: boolean;
}

/** `pairs` maps a theme index to the evidence index the learner put beside it. Solved when every theme has its own line. */
export function checkMatches(pairs: Record<number, number>, total: number): MatchCheck {
  const keys = Object.keys(pairs);
  const correct = keys.filter((k) => pairs[Number(k)] === Number(k)).length;
  return { missing: total - keys.length, correct, total, solved: keys.length === total && correct === total };
}

/** The table a solved match unlocks: one row per theme, the evidence for each chosen case, the question to judge. */
export function evidenceTable(w: WorkshopBlock, chosen: string[]): { head: string[]; rows: string[][] } {
  const first = chosen[0] ? w.editions[chosen[0]] : undefined;
  if (!first) return { head: [], rows: [] };
  if (chosen.length === 2) {
    const [a, b] = chosen;
    return {
      head: [capitalise(w.themeWord.singular), w.caseStudies[a]?.head ?? a, w.caseStudies[b]?.head ?? b, 'What you have to judge'],
      rows: first.themes.map((t, i) => [t.name, t.evidence, w.editions[b].themes[i]?.evidence ?? '', t.question]),
    };
  }
  return {
    head: [capitalise(w.themeWord.singular), 'Key evidence', 'What it did', 'What you have to judge'],
    rows: first.themes.map((t) => [t.term ? `${t.term}: ${t.name}` : t.name, t.evidence, t.matchLine, t.question]),
  };
}

// ---------------------------------------------------------------------------
// Step 5: compare (two cases), rank, choose the paragraph count, position.
// ---------------------------------------------------------------------------

/** Judgements per case study per theme: the index on the workshop's scale. */
export type Judgements = Record<string, Record<number, number>>;

export const compareCount = (j: Judgements, chosen: string[], n: number): number => chosen.reduce((acc, k) => acc + Array.from({ length: n }, (_, i) => j[k]?.[i]).filter((v) => v !== undefined).length, 0);
export const compareDone = (j: Judgements, chosen: string[], n: number): boolean => chosen.length === 2 && compareCount(j, chosen, n) === n * 2;

/** The judgement most cells of one case study got (at least half of them), or -1 for a mixed picture. */
export function mostJudged(j: Record<number, number> | undefined, n: number, scaleSize: number): number {
  const counts = Array.from({ length: scaleSize }, () => 0);
  for (let i = 0; i < n; i++) {
    const v = j?.[i];
    if (v !== undefined && v >= 0 && v < scaleSize) counts[v]++;
  }
  const need = Math.ceil(n / 2) + (n % 2 === 0 ? 1 : 0);
  for (let v = 0; v < scaleSize; v++) if (counts[v] >= need) return v;
  return -1;
}

/** "both the First World War and the Spanish Civil War mostly brought women a lasting gain", or the two apart. */
export function overallBoth(w: WorkshopBlock, e: WorkshopEdition, chosen: string[], j: Judgements): string {
  const n = e.themes.length;
  const scale = w.scale;
  const phrase = (k: string) => {
    const m = mostJudged(j[k], n, scale?.labels.length ?? 3);
    return m < 0 ? 'had mixed effects' : `mostly ${scale?.text[m] ?? scale?.labels[m] ?? ''}`;
  };
  const [a, b] = chosen;
  const vars = copyVars(w, chosen);
  const pa = phrase(a);
  const pb = phrase(b);
  return pa === pb ? copyFor(e, 'bothSame', { ...vars, j: pa }) : copyFor(e, 'bothDifferent', { ...vars, ja: pa, jb: pb });
}

/** The line under the compare grid once every cell is judged. */
export function compareSummary(w: WorkshopBlock, e: WorkshopEdition, chosen: string[], j: Judgements): string {
  const n = e.themes.length;
  const [a, b] = chosen;
  const same = Array.from({ length: n }, (_, i) => j[a]?.[i] !== undefined && j[a]?.[i] === j[b]?.[i]).filter(Boolean).length;
  return copyFor(e, 'compareSummary', { ...copyVars(w, chosen), same: String(same), themeCount: String(n), overallBoth: overallBoth(w, e, chosen, j) });
}

export interface HintInput {
  judgements: Judgements;
  chosen: string[];
  rank: number[];
  ranked: boolean;
  n: number;
  counterTheme?: number;
  anchorTheme?: number;
}

function clauseHolds(c: WorkshopHintClause, x: HintInput): boolean {
  const within = (v: number) => (c.min === undefined || v >= c.min) && (c.max === undefined || v <= c.max);
  if ('judgement' in c) {
    if (x.chosen.length !== 2) return false;
    const themes = Array.from({ length: x.n }, (_, i) => i).filter((i) => (c.themes === 'main' ? i !== x.counterTheme : c.themes === 'counter' ? i === x.counterTheme : true));
    const cases = c.caseStudy === 'first' ? [x.chosen[0]] : c.caseStudy === 'second' ? [x.chosen[1]] : x.chosen;
    let count = 0;
    for (const k of cases) for (const i of themes) if (x.judgements[k]?.[i] === c.judgement) count++;
    return within(count);
  }
  if (!x.ranked) return false;
  const theme = c.rankOf === 'anchor' ? x.anchorTheme : c.rankOf;
  if (theme === undefined) return false;
  const pos = x.rank.indexOf(theme);
  return pos >= 0 && within(pos);
}

/** The position the learner's judgements and ranking point at: the first hint whose clauses all hold, else the fallback. Null before there is anything to read. */
export function suggestPosition(hints: WorkshopHints, x: HintInput): number | null {
  const readable = (x.chosen.length === 2 && compareDone(x.judgements, x.chosen, x.n)) || x.ranked;
  if (!readable) return null;
  for (const h of hints.order) if (h.when.length > 0 && h.when.every((c) => clauseHolds(c, x))) return h.position;
  return hints.fallback;
}

/**
 * The themes that get a paragraph, in ranking order: the ones that must (the
 * main reason's, the counter-argument's, the anchor), then the highest ranked
 * until the chosen count is filled.
 */
export function paragraphThemes(rank: number[], paragraphs: number, must: Array<number | undefined>): number[] {
  const out: number[] = [];
  for (const m of must) if (m !== undefined && !out.includes(m)) out.push(m);
  for (const t of rank) if (out.length < paragraphs && !out.includes(t)) out.push(t);
  return out.sort((x, y) => rank.indexOf(x) - rank.indexOf(y));
}

export const leftOutThemes = (n: number, inList: number[]): number[] => Array.from({ length: n }, (_, i) => i).filter((i) => !inList.includes(i));

/** The thesis: the position's stem, "because" the reason, "although" the counter. Empty until there is a position and a reason. */
export function buildThesis(position: WorkshopPosition | undefined, reason: string, counter: string): string {
  if (!position || !reason.trim()) return '';
  let t = `${position.build}, because ${clean(reason)}`;
  if (counter.trim()) t += `, although ${clean(counter)}`;
  return `${t}.`;
}

export const clean = (x: string): string => String(x ?? '').trim().replace(/[.]+$/, '');

/** The reasons or counters of a position in the learner's ranking order of their themes. */
export function sortedByRank<T extends { theme: number }>(list: T[], rank: number[]): Array<T & { index: number }> {
  return list.map((x, index) => ({ ...x, index })).sort((x, y) => rank.indexOf(x.theme) - rank.indexOf(y.theme));
}

/** The rank tally line: the engine's, or the edition's, with the anchor named. */
export function rankTally(w: WorkshopBlock, e: WorkshopEdition, chosen: string[], paragraphs: number | null): string {
  if (!paragraphs) return 'Now choose how many body paragraphs you will write.';
  const vars = { ...copyVars(w, chosen, paragraphs), Anchor: capitalise(e.anchorTheme !== undefined ? e.themes[e.anchorTheme]?.low ?? '' : '') };
  return fillCopy(copyFor(e, 'rankTally', vars), vars);
}

export { numberWord };
